import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from '../NotificationBell';
import InstallAppButton from '../InstallAppButton';
import { LayoutDashboard, Music, Users, Tag, FileText, CreditCard, BarChart3, HelpCircle, User, LogOut, Menu, X, ChevronRight, Youtube, Shield, Moon, Sun } from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: { it: 'Dashboard', en: 'Dashboard' } },
  { to: '/releases', icon: Music, label: { it: 'Le Mie Release', en: 'My Releases' } },
  { to: '/artists', icon: Users, label: { it: 'I Miei Artisti', en: 'My Artists' } },
  { to: '/labels', icon: Tag, label: { it: 'Le Mie Label', en: 'My Labels' } },
  { to: '/streaming', icon: BarChart3, label: { it: 'Storico Ascolti', en: 'Streaming History' } },
  { to: '/statements', icon: FileText, label: { it: 'Rendiconti', en: 'Statements' } },
  { to: '/payments', icon: CreditCard, label: { it: 'Pagamenti', en: 'Payments' } },
  { to: '/oac', icon: Youtube, label: { it: 'Servizi Artista', en: 'Artist Services' } },
  { to: '/support', icon: HelpCircle, label: { it: 'Supporto', en: 'Support' } },
];

function SiteLogo({ site, className = '' }) {
  if (site?.site_logo_path) {
    const h = parseInt(site.site_logo_size) || 40;
    return <img src={site.site_logo_path} alt={site.site_name || 'Logo'} style={{ height: h + 'px' }} className={`max-w-[180px] object-contain ${className}`} />;
  }
  return <span className={`text-xl font-bold tracking-wider ${className}`}>{site?.site_name || 'PROUD NOIZE'}</span>;
}

export default function DashboardLayout() {
  const { user, site, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('pn-theme') === 'dark');
  const navigate = useNavigate();
  const lang = user?.language || 'it';
  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    if (dark) { document.body.classList.add('dark-mode'); } else { document.body.classList.remove('dark-mode'); }
    localStorage.setItem('pn-theme', dark ? 'dark' : 'light');
  }, [dark]);

  const NavContent = ({ onNav }) => (
    <>
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} onClick={onNav}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all text-sm
              ${isActive ? 'bg-[#E94560] text-white font-medium' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`}>
            <item.icon size={18} /><span>{item.label[lang]}</span>
          </NavLink>
        ))}
        {isAdmin && (
          <>
            <div className="border-t border-white/10 my-4" />
            <NavLink to="/admin" onClick={onNav} className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-yellow-400 hover:bg-yellow-400/10 font-medium">
              <Shield size={18} /><span>Admin Panel</span><ChevronRight size={14} className="ml-auto" />
            </NavLink>
          </>
        )}
      </nav>
      <div className="p-4 border-t border-white/10">
        {/* Installa App PWA */}
        <InstallAppButton />
        {/* Dark mode toggle */}
        <button onClick={() => setDark(d => !d)} className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/10 mb-3 transition">
          {dark ? <Sun size={16} className="text-yellow-400" /> : <Moon size={16} />}
          <span>{dark ? 'Light Mode' : 'Dark Mode'}</span>
          <div className={`ml-auto w-8 h-4 rounded-full transition-colors ${dark ? 'bg-yellow-500' : 'bg-gray-600'}`}>
            <div className={`w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${dark ? 'translate-x-4' : 'translate-x-0.5'} mt-[1px]`} />
          </div>
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#E94560] flex items-center justify-center text-sm font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p><p className="text-xs text-gray-400 truncate">{user?.email}</p></div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { onNav?.(); navigate('/profile'); }} className="flex-1 text-xs py-2 rounded bg-white/10 hover:bg-white/20 transition"><User size={14} className="inline mr-1" />Profilo</button>
          <button onClick={() => { onNav?.(); handleLogout(); }} className="flex-1 text-xs py-2 rounded bg-white/10 hover:bg-red-500/30 text-red-300 transition"><LogOut size={14} className="inline mr-1" />Esci</button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-[#1A1A2E] text-white fixed h-full z-30">
        <div className="p-6 border-b border-white/10"><SiteLogo site={site} /><p className="text-xs text-gray-400 mt-1">Workstation</p></div>
        <NavContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-72 bg-[#1A1A2E] text-white h-full flex flex-col overflow-y-auto">
            <div className="p-4 flex justify-between items-center border-b border-white/10"><SiteLogo site={site} /><button onClick={() => setSidebarOpen(false)} className="p-1"><X size={20} /></button></div>
            <NavContent onNav={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        <header className="lg:hidden bg-white shadow-sm px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-2"><Menu size={22} /></button>
          <SiteLogo site={site} className="text-[#1A1A2E]" />
          <div className="flex items-center gap-1"><NotificationBell /><button onClick={() => navigate('/profile')} className="p-2"><User size={22} /></button></div>
        </header>
        <div className="hidden lg:flex items-center justify-end gap-3 px-8 py-3 bg-white border-b sticky top-0 z-20">
          <NotificationBell />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-8 h-8 rounded-full bg-[#E94560] text-white flex items-center justify-center text-xs font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
            <span>{user?.first_name}</span>
          </div>
        </div>
        <main className="p-4 lg:p-8 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
